import { FC, useContext } from "react";
import { Form } from "react-final-form";
import { CheckoutContext } from "v3/context/checkout";
import { PaymentContext } from "v3/context/payment";
import { LocalizationContext } from "v3/context/localization";
import { TermsAndConditions } from "@pepdirect/v3/ui/TermsAndConditions";
import { SectionHeading } from "v3/components/checkout/SectionHeading";
import { OrderSpecs } from "v3/components/orderDetails/OrderSpecs";
import { CustomText } from "v3/services/graphql/generated";
import { PaymentDisplayDetails } from "v3/types/payment";
import {
  StyledAgreeToTermsText,
  StyledButton,
  StyledReviewSection,
  StyledSubmitButtonWrapper,
} from "./index.styled";

interface ReviewSectionProps {
  alwaysRequireCheckboxes?: boolean;
  editPayment: (shouldNotClearErrors?: boolean) => void;
  editShip: () => void;
  onSubmit: () => void;
  paymentDetails: NonNullable<PaymentDisplayDetails>;
  terms: CustomText[];
  validateTerms: (values: Record<string, boolean>) => Record<string, string>;
}

export const ReviewSection: FC<ReviewSectionProps> = ({
  alwaysRequireCheckboxes,
  editPayment,
  editShip,
  onSubmit,
  paymentDetails,
  terms,
  validateTerms,
}) => {
  const { checkoutSession, isLoading } = useContext(CheckoutContext);
  const { isBillingEqualShipping } = useContext(PaymentContext);
  const { localization } = useContext(LocalizationContext);

  const {
    agreeToTermsText = "Please agree to all terms to place your order.",
    headingText = "Review",
    submitOrderButtonText = "Place Order",
  } = localization?.checkout?.reviewStep || {};

  const {
    contactInfo,
    customerPo,
    giftInfo,
    requestedDeliveryDate,
    shippingMethodConfig,
    user,
    hasGasExchangeItems,
  } = checkoutSession || {};

  // something went wrong
  if (!contactInfo) return null;

  return (
    <StyledReviewSection>
      <SectionHeading>{headingText}</SectionHeading>

      <OrderSpecs
        contactInfo={contactInfo}
        editPayment={editPayment}
        editShip={editShip}
        giftInfo={giftInfo}
        isBillingEqualShipping={!!isBillingEqualShipping}
        paymentDetails={paymentDetails}
        poNumber={customerPo}
        requestedDeliveryDate={requestedDeliveryDate}
        shippingMethodName={shippingMethodConfig?.name}
      />

      {user && !alwaysRequireCheckboxes ? (
        <TermsAndConditions terms={terms} type="info">
          <StyledSubmitButtonWrapper>
            <StyledButton loading={isLoading} onClick={onSubmit}>
              {submitOrderButtonText}
            </StyledButton>
          </StyledSubmitButtonWrapper>
        </TermsAndConditions>
      ) : (
        <Form
          onSubmit={onSubmit}
          validate={validateTerms}
          render={({ handleSubmit, valid, submitFailed }) => (
            <form onSubmit={handleSubmit}>
              <TermsAndConditions
                hasGasExchangeItems={!!hasGasExchangeItems}
                terms={terms}
                localization={localization?.forms?.tos}
                submitFailed={submitFailed}
                type="optin"
                valid={valid}
              >
                <StyledSubmitButtonWrapper>
                  {!valid && (
                    <StyledAgreeToTermsText>
                      {agreeToTermsText}
                    </StyledAgreeToTermsText>
                  )}
                  <StyledButton
                    htmlType="submit"
                    loading={isLoading}
                    type={valid ? "primary" : "tertiary"}
                  >
                    {submitOrderButtonText}
                  </StyledButton>
                </StyledSubmitButtonWrapper>
              </TermsAndConditions>
            </form>
          )}
        />
      )}
    </StyledReviewSection>
  );
};
